import {
  CircleDollarSign,
  Coins,
  Eye,
  MousePointerClick,
  Target,
  TrendingUp,
  Users,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { MetricCard } from "./MetricCard";
import { MetricTrendChart } from "./MetricTrendChart";
import { useI18n } from "../i18n";
import type {
  DashboardResponse,
  Ga4SourceMediumRow,
  Ga4SourceMediumSeriesPoint,
  PaidSeriesPoint,
} from "../shared/contracts";

type ConversionOrigin = "all" | "google" | "microsoft" | "paid" | "organic" | "other";

type SourceGroup = "paid" | "organic" | "other";

interface ConversionSourceRow {
  key: string;
  sourceMedium: string;
  ecosystem: "google" | "microsoft" | "other";
  group: SourceGroup;
  sessions: number;
  keyEvents: number;
  previousKeyEvents: number | null;
  conversionRate: number | null;
}

interface ConversionTrendPoint {
  date: string;
  keyEvents: number;
  googleAds: number;
  microsoftAds: number;
}

interface AdsSourceRow {
  key: string;
  label: string;
  ecosystem: "google" | "microsoft";
  group: "paid";
  clicks: number;
  impressions: number;
  cost: number;
  conversions: number;
  conversionValue: number;
  cpa: number | null;
  roas: number | null;
}

const ORIGIN_OPTIONS: ConversionOrigin[] = [
  "all",
  "google",
  "microsoft",
  "paid",
  "organic",
  "other",
];

export function ConversionsView({ dashboard }: { dashboard: DashboardResponse }) {
  const { text, locale } = useI18n();
  const [origin, setOrigin] = useState<ConversionOrigin>("all");
  const [selectedSourceKeys, setSelectedSourceKeys] = useState<Set<string> | null>(null);
  const selectAllRef = useRef<HTMLInputElement>(null);
  const ga4 = dashboard.ga4;
  const googleAds = dashboard.googleAds;
  const microsoftAds = dashboard.microsoftAds;

  const allSourceRows = useMemo(
    () =>
      (ga4?.firstUserSourceMedium?.rows || [])
        .map(toConversionSourceRow),
    [ga4?.firstUserSourceMedium?.rows],
  );
  const sourceRows = useMemo(
    () => allSourceRows.filter((row) => matchesOrigin(row, origin)),
    [allSourceRows, origin],
  );
  useEffect(() => {
    setSelectedSourceKeys(null);
  }, [allSourceRows]);
  const selectedSourceRows = useMemo(
    () =>
      sourceRows.filter(
        (row) => selectedSourceKeys === null || selectedSourceKeys.has(row.key),
      ),
    [selectedSourceKeys, sourceRows],
  );
  const allVisibleSourcesSelected =
    sourceRows.length > 0 && selectedSourceRows.length === sourceRows.length;

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate =
        selectedSourceRows.length > 0 && !allVisibleSourcesSelected;
    }
  }, [allVisibleSourcesSelected, selectedSourceRows.length]);

  const toggleAllVisibleSources = () => {
    setSelectedSourceKeys((current) => {
      const next = new Set(
        current === null ? allSourceRows.map((row) => row.key) : current,
      );
      if (allVisibleSourcesSelected) {
        sourceRows.forEach((row) => next.delete(row.key));
      } else {
        sourceRows.forEach((row) => next.add(row.key));
      }
      return next;
    });
  };

  const toggleSource = (sourceKey: string) => {
    setSelectedSourceKeys((current) => {
      const next = new Set(
        current === null ? allSourceRows.map((row) => row.key) : current,
      );
      if (next.has(sourceKey)) next.delete(sourceKey);
      else next.add(sourceKey);
      return next;
    });
  };

  const adsRows = useMemo(
    () =>
      [
        googleAds ? toAdsSourceRow("google", text.conversions.googleAds, googleAds) : null,
        microsoftAds
          ? toAdsSourceRow("microsoft", text.conversions.microsoftAds, microsoftAds)
          : null,
      ].filter((row): row is AdsSourceRow =>
        Boolean(
          row &&
            matchesOrigin(row, origin) &&
            selectedSourceRows.some(
              (sourceRow) =>
                sourceRow.group === "paid" && sourceRow.ecosystem === row.ecosystem,
            ),
        ),
      ),
    [
      googleAds,
      microsoftAds,
      origin,
      selectedSourceRows,
      text.conversions.googleAds,
      text.conversions.microsoftAds,
    ],
  );
  const sourceTotals = sumSourceRows(selectedSourceRows);
  const adsTotals = sumAdsRows(adsRows);
  const trend = buildConversionTrend(
    ga4?.firstUserSourceMedium?.series,
    ga4?.firstUserSourceMedium?.compareSeries,
    googleAds?.series,
    googleAds?.compareSeries,
    microsoftAds?.series,
    microsoftAds?.compareSeries,
    origin,
    selectedSourceRows,
  );
  const totalKeyEvents = sourceRows.length ? sourceTotals.keyEvents : null;
  const totalSessions = sourceRows.length ? sourceTotals.sessions : null;
  const paidKeyEvents = sumSourceRows(
    selectedSourceRows.filter((row) => row.group === "paid"),
  ).keyEvents;
  const organicKeyEvents = sumSourceRows(
    selectedSourceRows.filter((row) => row.group === "organic"),
  ).keyEvents;
  const otherKeyEvents = sumSourceRows(
    selectedSourceRows.filter((row) => row.group === "other"),
  ).keyEvents;
  const showGoogleAds = origin === "all" || origin === "google" || origin === "paid";
  const showMicrosoftAds =
    origin === "all" || origin === "microsoft" || origin === "paid";

  return (
    <>
      <section className="conversion-filter-bar">
        <div>
          <span className="conversion-filter-eyebrow">{text.conversions.filterLabel}</span>
          <strong>{text.conversions.filterHelp}</strong>
        </div>
        <label className="conversion-origin-control">
          <Target size={15} />
          <span className="sr-only">{text.conversions.origin}</span>
          <select value={origin} onChange={(event) => setOrigin(event.target.value as ConversionOrigin)}>
            {ORIGIN_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {text.conversions.origins[option]}
              </option>
            ))}
          </select>
        </label>
      </section>

      <section className="metric-grid conversion-metric-grid">
        <MetricCard
          label={text.conversions.keyEvents}
          metric={{
            value: totalKeyEvents,
            previous: origin === "all" ? ga4?.previous?.conversions : sourceTotals.previousKeyEvents,
          }}
          icon={Target}
          tone="green"
        />
        <MetricCard
          label={text.conversions.paidKeyEvents}
          metric={{ value: sourceRows.length ? paidKeyEvents : null }}
          icon={CircleDollarSign}
          tone="amber"
        />
        <MetricCard
          label={text.conversions.organicKeyEvents}
          metric={{ value: sourceRows.length ? organicKeyEvents : null }}
          icon={TrendingUp}
          tone="blue"
        />
        <MetricCard
          label={text.conversions.otherKeyEvents}
          metric={{ value: sourceRows.length ? otherKeyEvents : null }}
          icon={Users}
          tone="ink"
        />
        {showGoogleAds ? (
          <MetricCard
            label={text.conversions.googleAdsConversions}
            metric={{ value: googleAds?.conversions ?? null, previous: googleAds?.previous?.conversions }}
            icon={MousePointerClick}
            tone="red"
          />
        ) : null}
        {showMicrosoftAds ? (
          <MetricCard
            label={text.conversions.microsoftAdsConversions}
            metric={{ value: microsoftAds?.conversions ?? null, previous: microsoftAds?.previous?.conversions }}
            icon={MousePointerClick}
            tone="blue"
          />
        ) : null}
      </section>

      <section className="view-grid conversions-grid">
        <article className="panel span-2 chart-panel">
          <PanelHeading title={text.conversions.trendTitle} meta={text.conversions.trendMeta} />
          {trend.points.length ? (
            <MetricTrendChart
              points={trend.points}
              comparePoints={trend.comparePoints}
              compareLabel={formatCompareLabel(dashboard, text)}
              compareAlignBy={dashboard.filters.compareAlignBy}
              xKey="date"
              series={trend.series}
            />
          ) : (
            <SourceEmpty title={text.conversions.noTrend} />
          )}
        </article>
        <article className="panel conversion-summary-panel">
          <PanelHeading title={text.conversions.summaryTitle} meta={text.conversions.summaryMeta} />
          <div className="technical-table">
            <TechnicalRow label={text.conversions.sessions} value={formatNumber(totalSessions, locale)} good={totalSessions !== null} />
            <TechnicalRow label={text.conversions.keyEvents} value={formatNumber(totalKeyEvents, locale)} good={totalKeyEvents !== null} />
            <TechnicalRow label={text.conversions.rate} value={formatPercent(totalSessions ? (totalKeyEvents || 0) / totalSessions : null)} good={Boolean(totalSessions)} />
            <TechnicalRow label={text.conversions.adsConversions} value={formatNumber(adsTotals.conversions, locale)} good={adsTotals.conversions > 0} />
            <TechnicalRow label={text.conversions.adsValue} value={formatCurrency(adsTotals.conversionValue, locale)} good={adsTotals.conversionValue > 0} />
          </div>
        </article>

        <article className="panel span-3 conversion-source-panel">
          <PanelHeading title={text.conversions.sourceBreakdown} meta={text.conversions.ga4Meta} />
          {sourceRows.length ? (
            <div className="query-table-wrap">
              <table className="query-table conversion-table">
                <thead>
                  <tr>
                    <th className="conversion-select-column">
                      <input
                        ref={selectAllRef}
                        type="checkbox"
                        checked={allVisibleSourcesSelected}
                        onChange={toggleAllVisibleSources}
                        disabled={!sourceRows.length}
                        aria-label={
                          allVisibleSourcesSelected
                            ? text.conversions.deselectAllSources
                            : text.conversions.selectAllSources
                        }
                      />
                    </th>
                    <th>{text.conversions.sourceMedium}</th>
                    <th>{text.conversions.origin}</th>
                    <th>{text.conversions.sessions}</th>
                    <th>{text.conversions.keyEvents}</th>
                    <th>{text.conversions.rate}</th>
                    <th>{text.conversions.previous}</th>
                  </tr>
                </thead>
                <tbody>
                  {sourceRows.slice(0, 50).map((row) => (
                    <tr key={row.key}>
                      <td className="conversion-select-cell">
                        <input
                          type="checkbox"
                          checked={
                            selectedSourceKeys === null || selectedSourceKeys.has(row.key)
                          }
                          onChange={() => toggleSource(row.key)}
                          aria-label={`${text.conversions.selectSource}: ${row.sourceMedium}`}
                        />
                      </td>
                      <td>{row.sourceMedium}</td>
                      <td><OriginBadge group={row.group} ecosystem={row.ecosystem} labels={text.conversions} /></td>
                      <td>{formatNumber(row.sessions, locale)}</td>
                      <td>{formatNumber(row.keyEvents, locale)}</td>
                      <td>{formatPercent(row.conversionRate)}</td>
                      <td>{formatNumber(row.previousKeyEvents, locale)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <SourceEmpty title={text.conversions.noGa4Data} />
          )}
        </article>

        <article className="panel span-3 conversion-ads-panel">
          <PanelHeading title={text.conversions.adsBreakdown} meta={text.conversions.adsMeta} />
          {adsRows.length ? (
            <div className="query-table-wrap">
              <table className="query-table conversion-table">
                <thead>
                  <tr>
                    <th>{text.conversions.source}</th>
                    <th>{text.conversions.clicks}</th>
                    <th>{text.conversions.impressions}</th>
                    <th>{text.conversions.cost}</th>
                    <th>{text.conversions.adsConversions}</th>
                    <th>{text.conversions.cpa}</th>
                    <th>{text.conversions.roas}</th>
                  </tr>
                </thead>
                <tbody>
                  {adsRows.map((row) => (
                    <tr key={row.key}>
                      <td><OriginBadge group="paid" ecosystem={row.ecosystem} labels={text.conversions} label={row.label} /></td>
                      <td>{formatNumber(row.clicks, locale)}</td>
                      <td>{formatNumber(row.impressions, locale)}</td>
                      <td>{formatCurrency(row.cost, locale)}</td>
                      <td>{formatNumber(row.conversions, locale)}</td>
                      <td>{formatCurrency(row.cpa, locale)}</td>
                      <td>{row.roas === null ? "—" : row.roas.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <SourceEmpty title={text.conversions.noAdsData} />
          )}
        </article>
      </section>
      <p className="panel-note conversion-note">{text.conversions.note}</p>
    </>
  );
}

function toConversionSourceRow(row: Ga4SourceMediumRow): ConversionSourceRow {
  const sourceMedium = row.sourceMedium || "Unassigned";
  const normalized = sourceMedium.toLowerCase();
  const ecosystem = normalized.includes("google") || normalized.includes("youtube")
    ? "google"
    : normalized.includes("bing") || normalized.includes("microsoft") || normalized.includes("msn")
      ? "microsoft"
      : "other";
  const group: SourceGroup = /cpc|ppc|paid|display|shopping|cross-network|ads/.test(normalized)
    ? "paid"
    : /organic/.test(normalized)
      ? "organic"
      : "other";
  return {
    key: sourceMedium,
    sourceMedium,
    ecosystem,
    group,
    sessions: row.sessions,
    keyEvents: row.conversions,
    previousKeyEvents: row.previous?.conversions ?? null,
    conversionRate: row.conversionRate,
  };
}

function toAdsSourceRow(
  ecosystem: "google" | "microsoft",
  label: string,
  result: NonNullable<DashboardResponse["googleAds"]>,
): AdsSourceRow {
  return {
    key: ecosystem,
    label,
    ecosystem,
    group: "paid",
    clicks: result.clicks,
    impressions: result.impressions,
    cost: result.cost,
    conversions: result.conversions,
    conversionValue: result.conversionValue,
    cpa: result.cpa,
    roas: result.roas,
  };
}

function matchesOrigin(row: { ecosystem: string; group?: SourceGroup }, origin: ConversionOrigin) {
  if (origin === "all") return true;
  if (origin === "google" || origin === "microsoft") return row.ecosystem === origin;
  return row.group === origin;
}

function sumSourceRows(rows: ConversionSourceRow[]) {
  const total = rows.reduce(
    (total, row) => ({
      sessions: total.sessions + row.sessions,
      keyEvents: total.keyEvents + row.keyEvents,
      previousKeyEvents: total.previousKeyEvents + (row.previousKeyEvents ?? 0),
    }),
    { sessions: 0, keyEvents: 0, previousKeyEvents: 0 },
  );
  return {
    ...total,
    previousKeyEvents: rows.some((row) => row.previousKeyEvents !== null)
      ? total.previousKeyEvents
      : null,
  };
}

function sumAdsRows(rows: AdsSourceRow[]) {
  return rows.reduce(
    (total, row) => ({
      conversions: total.conversions + row.conversions,
      conversionValue: total.conversionValue + row.conversionValue,
    }),
    { conversions: 0, conversionValue: 0 },
  );
}

function buildConversionTrend(
  ga4Points: Ga4SourceMediumSeriesPoint[] | undefined,
  ga4ComparePoints: Ga4SourceMediumSeriesPoint[] | undefined,
  googlePoints: PaidSeriesPoint[] | undefined,
  googleComparePoints: PaidSeriesPoint[] | undefined,
  microsoftPoints: PaidSeriesPoint[] | undefined,
  microsoftComparePoints: PaidSeriesPoint[] | undefined,
  origin: ConversionOrigin,
  selectedSourceRows: ConversionSourceRow[],
) {
  const selectedSourceKeys = new Set(
    selectedSourceRows.map((row) => row.key),
  );
  const showGoogleAds = selectedSourceRows.some(
    (row) => row.ecosystem === "google" && row.group === "paid",
  );
  const showMicrosoftAds = selectedSourceRows.some(
    (row) => row.ecosystem === "microsoft" && row.group === "paid",
  );
  const series = [
    { key: "keyEvents", label: "GA4 Key Events", color: "#087a5a", area: true },
    ...(showGoogleAds && (origin === "all" || origin === "google" || origin === "paid")
      ? [{ key: "googleAds", label: "Google Ads", color: "#b7473e", axis: 1 as const }]
      : []),
    ...(showMicrosoftAds &&
    (origin === "all" || origin === "microsoft" || origin === "paid")
      ? [{ key: "microsoftAds", label: "Microsoft Ads", color: "#176bb1", axis: 1 as const }]
      : []),
  ];
  return {
    points: aggregateConversionTrend(
      ga4Points,
      googlePoints,
      microsoftPoints,
      origin,
      selectedSourceKeys,
    ),
    comparePoints: aggregateConversionTrend(
      ga4ComparePoints,
      googleComparePoints,
      microsoftComparePoints,
      origin,
      selectedSourceKeys,
    ),
    series,
  };
}

function aggregateConversionTrend(
  ga4Points: Ga4SourceMediumSeriesPoint[] | undefined,
  googlePoints: PaidSeriesPoint[] | undefined,
  microsoftPoints: PaidSeriesPoint[] | undefined,
  origin: ConversionOrigin,
  selectedSourceKeys: Set<string>,
) {
  const dates = new Map<string, ConversionTrendPoint>();
  const getPoint = (date: string) => {
    const current =
      dates.get(date) ||
      { date, keyEvents: 0, googleAds: 0, microsoftAds: 0 };
    dates.set(date, current);
    return current;
  };
  for (const point of ga4Points || []) {
    const sourceRow = toConversionSourceRow({
      sourceMedium: point.sourceMedium,
      sessions: point.sessions,
      conversions: point.conversions,
      users: 0,
      conversionRate: point.sessions ? point.conversions / point.sessions : null,
    });
    if (
      selectedSourceKeys.has(sourceRow.key) &&
      matchesOrigin(sourceRow, origin)
    ) {
      getPoint(point.date).keyEvents += point.conversions;
    }
  }
  if (origin === "all" || origin === "google" || origin === "paid") {
    for (const point of googlePoints || []) {
      getPoint(point.date).googleAds += point.conversions;
    }
  }
  if (origin === "all" || origin === "microsoft" || origin === "paid") {
    for (const point of microsoftPoints || []) {
      getPoint(point.date).microsoftAds += point.conversions;
    }
  }
  return [...dates.values()].sort((a, b) => a.date.localeCompare(b.date));
}

function formatCompareLabel(
  dashboard: DashboardResponse,
  text: ReturnType<typeof useI18n>["text"],
) {
  const compare = dashboard.dateRanges.compare;
  return compare
    ? text.date.compareModes[compare.mode]
    : text.dashboard.noComparison;
}

function OriginBadge({
  group,
  ecosystem,
  labels,
  label,
}: {
  group: SourceGroup;
  ecosystem: "google" | "microsoft" | "other";
  labels: ReturnType<typeof useI18n>["text"]["conversions"];
  label?: string;
}) {
  return <span className={`conversion-badge ${group} ${ecosystem}`}>{label || labels.ecosystems[ecosystem]}</span>;
}

function PanelHeading({ title, meta }: { title: string; meta: string }) {
  return (
    <div className="panel-heading">
      <div>
        <strong>{title}</strong>
        <span>{meta}</span>
      </div>
      <Target size={17} />
    </div>
  );
}

function TechnicalRow({ label, value, good }: { label: string; value: string; good: boolean }) {
  return (
    <div className="technical-row">
      <span>{label}</span>
      <strong title={value}>{value}</strong>
      <i className={good ? "good" : "warning"} />
    </div>
  );
}

function SourceEmpty({ title }: { title: string }) {
  return <div className="source-empty"><Target size={19} /><span>{title}</span></div>;
}

function formatNumber(value: number | null | undefined, locale: string) {
  return value === null || value === undefined
    ? "—"
    : new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value);
}

function formatCurrency(value: number | null | undefined, locale: string) {
  return value === null || value === undefined
    ? "—"
    : new Intl.NumberFormat(locale, { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(value);
}

function formatPercent(value: number | null | undefined) {
  return value === null || value === undefined ? "—" : `${(value * 100).toFixed(1)}%`;
}
